import axios from 'axios'
import { useMemo } from 'react'
import { useQuery, UseQueryResult } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../config/queryClient'
import { fetchImportedInvoiceById } from '../query-api'
import { ImportedInvoice } from '../types/importedInvoice'
import { getImportedInvoice } from '../utils/getImportedInvoice'
import { getImportedInvoiceQueryKey } from '../utils/getImportedInvoiceQueryKey'

// rest-invoice types the path param `int?`. Anything else in the URL is not an id it can resolve,
// and must never reach the request path.
const IMPORTED_INVOICE_ID_PATTERN = /^\d+$/

// `qs` parses a repeated param into an array, whatever the filters type says.
const isImportedInvoiceId = (value: unknown): value is string =>
  typeof value === 'string' && IMPORTED_INVOICE_ID_PATTERN.test(value)

const isNotFoundError = (error: unknown): boolean => axios.isAxiosError(error) && error.response?.status === 404

interface UseFetchImportedInvoiceByIdOptions {
  isEnabled?: boolean
}

type ImportedInvoiceQueryResult = UseQueryResult<Awaited<ReturnType<typeof fetchImportedInvoiceById>> | null>

interface UseFetchImportedInvoiceByIdResult extends Omit<ImportedInvoiceQueryResult, 'data'> {
  invoice?: ImportedInvoice
  isNotFound: boolean
}

/**
 * A 404 is an answer, not a failure: the query resolves it to `null` and reports `isNotFound`, so
 * it is neither retried nor reported. An id that isn't numeric is reported the same way without
 * sending a request, so `isNotFound` covers both a 404 and a malformed id.
 *
 * Every error is forwarded to `trackError` by the react-query logger, so callers don't report
 * errors themselves.
 */
export const useFetchImportedInvoiceById = (
  importedInvoiceId?: string,
  { isEnabled = true }: UseFetchImportedInvoiceByIdOptions = {},
): UseFetchImportedInvoiceByIdResult => {
  // apiZervant only gets the `organizations/:id` prefix once the organization has loaded.
  const { organization } = useUserOrganization()
  const organizationId = organization?.id
  const isIdValid = isImportedInvoiceId(importedInvoiceId)

  const { data: invoiceResponse, ...rest } = useQuery(
    getImportedInvoiceQueryKey(organizationId, importedInvoiceId),
    async () => {
      if (!isImportedInvoiceId(importedInvoiceId)) {
        return null
      }

      try {
        return await fetchImportedInvoiceById(importedInvoiceId)
      } catch (fetchError) {
        if (isNotFoundError(fetchError)) {
          return null
        }

        throw fetchError
      }
    },
    {
      enabled: !!organizationId && isEnabled && isIdValid,
      staleTime: defaultStaleTime,
    },
  )

  const invoiceData = invoiceResponse?.data
  const invoice = useMemo(() => (invoiceData ? getImportedInvoice(invoiceData) : undefined), [invoiceData])
  const isNotFound = invoiceResponse === null || (isEnabled && Boolean(importedInvoiceId) && !isIdValid)

  return { ...rest, invoice, isNotFound }
}
